import type {MantineTheme} from '@mantine/core';
import {parseThemeColor} from '@mantine/core';
import type {AssistantChartResponse} from 'src/api/ProactApiClient';
import {AssistantChartTypeEnum} from 'src/api/ProactApiClient';
import type {ChatChartLayout} from 'src/app/assistant/Chart/chatChartFormat';
import {
  CHART_VERTICAL_MARGIN,
  CHAT_CHART_COLORS,
  DEFAULT_X_AXIS_HEIGHT,
  LEGEND_HEIGHT,
  chooseChartLayout,
  isDisplayableChart,
} from 'src/app/assistant/Chart/chatChartFormat';

// Fits the printable width of both Letter (695px) and A4 (673px) inside 16mm margins.
export const PRINT_CHART_WIDTH = 640;
// Letter's printable height is 935px; this leaves room for the title and legend.
export const MAX_PRINT_CHART_HEIGHT = 800;
// A 12px tick label needs about 14px; below 16px per row, labels touch.
export const MIN_PRINT_ROW_HEIGHT = 16;

export const PRINT_CHART_TOO_TALL_TEXT = 'Too many rows to chart; data below.';

export type PrintChartPlan =
  | {kind: 'table'}
  | {kind: 'too-tall'}
  | {kind: 'pie'; chart: AssistantChartResponse}
  | {
      kind: 'cartesian';
      chart: AssistantChartResponse;
      layout: ChatChartLayout;
      svgHeight: number;
    };

/** The print document has no Mantine CSS variables, so palette colors resolve to literals. */
export const resolvePrintChartColors = (theme: MantineTheme): string[] =>
  CHAT_CHART_COLORS.map(color => parseThemeColor({color, theme}).value);

/**
 * Uses the on-screen layout at print width, shrinking horizontal-bar rows to fit one page;
 * falls back to table-only when the chart cannot be drawn or has too many rows.
 */
export const planPrintChart = (chart: AssistantChartResponse): PrintChartPlan => {
  if (!isDisplayableChart(chart)) return {kind: 'table'};
  if (chart.type === AssistantChartTypeEnum.Pie) return {kind: 'pie', chart};
  const layout = chooseChartLayout(chart, PRINT_CHART_WIDTH);
  const legendHeight = chart.series.length >= 2 ? LEGEND_HEIGHT : 0;
  const svgHeight = layout.height - legendHeight;
  if (svgHeight <= MAX_PRINT_CHART_HEIGHT) {
    return {kind: 'cartesian', chart, layout, svgHeight};
  }
  const rowSpace =
    MAX_PRINT_CHART_HEIGHT - DEFAULT_X_AXIS_HEIGHT - CHART_VERTICAL_MARGIN;
  if (
    layout.kind !== 'rows' ||
    rowSpace / chart.points.length < MIN_PRINT_ROW_HEIGHT
  ) {
    return {kind: 'too-tall'};
  }
  return {kind: 'cartesian', chart, layout, svgHeight: MAX_PRINT_CHART_HEIGHT};
};
